import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Truck, RefreshCw, Shield, Star } from 'lucide-react';
import Header from './components/Header10';
import Footer from './components/Footer10';
import Button from './components/Button';
import ProductCard from './components/ProductCard';

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedSize, setSelectedSize] = useState('M');
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);

    const product = {
        id: id,
        name: 'Minimalist Tote Bag',
        price: 2499,
        originalPrice: 3499,
        rating: 4.5,
        reviews: 128,
        description: 'A timeless tote bag crafted from premium materials. Perfect for everyday use with its minimalist design and spacious interior.',
        features: [
            'Premium quality leather',
            'Spacious main compartment',
            'Interior zip pocket',
            'Adjustable strap',
            'Minimalist design',
        ],
        images: [
            'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=800',
            'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=800',
            'https://images.unsplash.com/photo-1594633313593-bab3825d0caf?w=800',
            'https://images.unsplash.com/photo-1564422170194-896b89110ef8?w=800',
        ],
        sizes: ['S', 'M', 'L'],
        inStock: true,
    };

    const relatedProducts = [
        {
            id: 2,
            name: 'Canvas Backpack',
            category: 'Bags',
            price: 2299,
            image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=600',
        },
        {
            id: 3,
            name: 'Crossbody Bag',
            category: 'Bags',
            price: 1999,
            image: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600',
        },
        {
            id: 4,
            name: 'Leather Wallet',
            category: 'Accessories',
            price: 1499,
            image: 'https://images.unsplash.com/photo-1627123424574-724758594e93?w=600',
        },
        {
            id: 5,
            name: 'Sunglasses',
            category: 'Accessories',
            price: 1899,
            image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=600',
        },
    ];

    return (
        <div className="min-h-screen bg-white">
            <Header showBackButton={true} cartCount={3} />

            <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
                {/* Product Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
                    {/* Images */}
                    <div>
                        {/* Main Image */}
                        <div className="bg-gray-100 mb-4 overflow-hidden">
                            <img
                                src={product.images[selectedImage]}
                                alt={product.name}
                                className="w-full h-auto"
                            />
                        </div>

                        {/* Thumbnail Gallery */}
                        <div className="grid grid-cols-4 gap-4">
                            {product.images.map((image, index) => (
                                <button
                                    key={index}
                                    onClick={() => setSelectedImage(index)}
                                    className={`bg-gray-100 overflow-hidden border-2 ${
                                        selectedImage === index ? 'border-gray-900' : 'border-transparent'
                                    }`}
                                >
                                    <img src={image} alt={`View ${index + 1}`} className="w-full h-auto" />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Product Info */}
                    <div>
                        <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>

                        {/* Rating */}
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex gap-1">
                                {[...Array(5)].map((_, i) => (
                                    <Star
                                        key={i}
                                        size={18}
                                        className={i < Math.floor(product.rating) ? 'fill-gray-900 text-gray-900' : 'text-gray-300'}
                                    />
                                ))}
                            </div>
                            <span className="text-sm text-gray-600">({product.reviews} reviews)</span>
                        </div>

                        {/* Price */}
                        <div className="flex items-center gap-3 mb-6">
                            <span className="text-3xl font-bold text-gray-900">₹{product.price}</span>
                            {product.originalPrice && (
                                <>
                                    <span className="text-xl text-gray-500 line-through">₹{product.originalPrice}</span>
                                    <span className="bg-gray-900 text-white px-3 py-1 text-sm font-medium rounded">
                                        {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
                                    </span>
                                </>
                            )}
                        </div>

                        {/* Description */}
                        <p className="text-gray-600 mb-6 leading-relaxed">{product.description}</p>

                        {/* Size Selector */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-900 mb-3">Select Size</label>
                            <div className="flex gap-3">
                                {product.sizes.map(size => (
                                    <button
                                        key={size}
                                        onClick={() => setSelectedSize(size)}
                                        className={`px-6 py-3 border-2 rounded-lg font-medium transition-colors ${
                                            selectedSize === size
                                                ? 'border-gray-900 bg-gray-900 text-white'
                                                : 'border-gray-300 hover:border-gray-900'
                                        }`}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Quantity */}
                        <div className="mb-8">
                            <label className="block text-sm font-medium text-gray-900 mb-3">Quantity</label>
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    className="w-12 h-12 border border-gray-300 rounded-lg hover:border-gray-900 font-medium"
                                >
                                    -
                                </button>
                                <span className="text-xl font-medium w-12 text-center">{quantity}</span>
                                <button
                                    onClick={() => setQuantity(quantity + 1)}
                                    className="w-12 h-12 border border-gray-300 rounded-lg hover:border-gray-900 font-medium"
                                >
                                    +
                                </button>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-4 mb-8">
                            <Button variant="primary" size="lg" fullWidth={true} onClick={() => navigate('/template1/cart')}>
                                <ShoppingCart size={20} className="mr-2" />
                                Add to Cart
                            </Button>
                            <button className="w-14 h-14 border-2 border-gray-900 rounded-lg flex items-center justify-center hover:bg-gray-900 hover:text-white transition-colors">
                                <Heart size={20} />
                            </button>
                        </div>

                        {/* Features */}
                        <div className="border-t border-gray-200 pt-6">
                            <h3 className="font-semibold mb-4">Features</h3>
                            <ul className="space-y-2">
                                {product.features.map((feature, index) => (
                                    <li key={index} className="flex items-start gap-2 text-gray-600">
                                        <span className="text-gray-900">•</span>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Info Cards */}
                        <div className="grid grid-cols-3 gap-4 mt-8">
                            <div className="text-center p-4 bg-gray-50 rounded-lg">
                                <Truck size={24} className="mx-auto mb-2 text-gray-900" />
                                <p className="text-xs text-gray-600">Free Shipping</p>
                            </div>
                            <div className="text-center p-4 bg-gray-50 rounded-lg">
                                <RefreshCw size={24} className="mx-auto mb-2 text-gray-900" />
                                <p className="text-xs text-gray-600">Easy Returns</p>
                            </div>
                            <div className="text-center p-4 bg-gray-50 rounded-lg">
                                <Shield size={24} className="mx-auto mb-2 text-gray-900" />
                                <p className="text-xs text-gray-600">Secure Payment</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Related Products */}
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-8">You May Also Like</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {relatedProducts.map(product => (
                            <ProductCard key={product.id} product={product} />
                        ))}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default ProductDetail;
